// ══════════════════════════════════════════════════════════════
// YOUTUBE (agregado 06/10/2026)
//
// Helpers para el video opcional de las Noticias. En Firestore se
// guarda el link tal cual lo pega el admin (campo "Video"); acá se
// extrae el ID del video para armar el reproductor y la miniatura.
//
// Formatos aceptados:
//   https://www.youtube.com/watch?v=ID
//   https://youtu.be/ID
//   https://www.youtube.com/shorts/ID
//   https://www.youtube.com/live/ID
//   https://www.youtube.com/embed/ID
//   m.youtube.com / music.youtube.com / youtube-nocookie.com
//   el ID solo (11 caracteres)
// También respeta el minuto de inicio (?t=90, ?t=1m30s, ?start=90).
// ══════════════════════════════════════════════════════════════

const ID_PATTERN = /^[A-Za-z0-9_-]{11}$/;

const toUrl = (value: string): URL | null => {
  try {
    return new URL(/^https?:\/\//i.test(value) ? value : `https://${value}`);
  } catch {
    return null;
  }
};

/** Devuelve el ID del video, o null si el texto no es un link de YouTube válido. */
export const getYouTubeId = (input?: string | null): string | null => {
  const value = (input ?? "").trim();
  if (!value) return null;
  if (ID_PATTERN.test(value)) return value;

  const url = toUrl(value);
  if (!url) return null;

  const host = url.hostname.toLowerCase().replace(/^(www\.|m\.|music\.)/, "");
  let candidate: string | null = null;

  if (host === "youtu.be") {
    candidate = url.pathname.split("/")[1] || null;
  } else if (host === "youtube.com" || host === "youtube-nocookie.com") {
    if (url.pathname === "/watch") {
      candidate = url.searchParams.get("v");
    } else {
      const [, kind, id] = url.pathname.split("/");
      if (["embed", "shorts", "live", "v"].includes(kind)) {
        candidate = id || null;
      }
    }
  }

  return candidate && ID_PATTERN.test(candidate) ? candidate : null;
};

/** Segundo de inicio indicado en el link (?t= o ?start=), o 0. */
export const getYouTubeStart = (input?: string | null): number => {
  const url = toUrl((input ?? "").trim());
  if (!url) return 0;
  const raw = url.searchParams.get("t") ?? url.searchParams.get("start");
  if (!raw) return 0;
  if (/^\d+$/.test(raw)) return Number(raw);

  const match = raw.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/);
  if (!match) return 0;
  const [, h = "0", m = "0", s = "0"] = match;
  return Number(h) * 3600 + Number(m) * 60 + Number(s);
};

/** URL del reproductor embebido (dominio sin cookies de YouTube). */
export const buildYouTubeEmbedUrl = (id: string, start = 0): string => {
  const params = new URLSearchParams({
    autoplay: "1",
    rel: "0",
    playsinline: "1",
    modestbranding: "1",
  });
  if (start > 0) params.set("start", String(start));
  return `https://www.youtube-nocookie.com/embed/${id}?${params.toString()}`;
};

export const buildYouTubeThumbnailUrl = (id: string): string =>
  `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;

export const buildYouTubeWatchUrl = (id: string): string =>
  `https://www.youtube.com/watch?v=${id}`;
